import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';

import {
  ManageClauseRequestSchema,
  type SearchAlias,
  type SearchAliasListRequest,
  type SearchAliasListResponse,
  type SearchAliasMineResponse,
  type SearchAliasStatus,
} from '@clm/shared-types';

import { manageClause } from '../../api/clauses';
import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';
import { listSearchAliases, mineSearchAliases } from '../../api/search-aliases';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { ErrorNotice } from '../../components/ErrorNotice';
import { EMPTY_CURSOR_STACK, pageNumber, popCursor, pushCursor, type CursorStack } from '../../components/cursor-stack';

/**
 * Aliases — the sixth panel on Search quality · FR-26 (alias mining half), NFR-13 · CLM-59.
 *
 * The curated query vocabulary and its review queue. Three statuses, all
 * derived server-side: **suggested** (proposed by mining or by a reviewer, not
 * yet approved), **active** (live in query expansion on the very next search),
 * **inactive** (approved once, since deactivated). Approve / Deactivate /
 * Reactivate are the three `manage-clause` alias actions; *Add an alias* is
 * `create_search_alias`, which lands as a *suggestion* — creating is proposing,
 * approving is deciding, two clicks by the same Legal user is the intended shape.
 * *Mine proposals* runs `POST /v1/search/aliases/mine`: the system proposes,
 * never activates.
 *
 * ## Re-fetch, never patch
 *
 * Every mutation's `onSettled` bumps `reloadNonce` and the list is read again.
 * The write response carries the ratified `ClauseSearchAliasSchema`, which has
 * no `approved_*` keys, and a second reviewer may have acted — the server's
 * post-image is the only honest row.
 *
 * ## Nothing auto-runs
 *
 * Mining is a governance write; it runs on the button and never on render.
 *
 * ## Roles
 *
 * The list is admin ∪ Legal (`clause:review_match`); every write is
 * `legal_reviewer` alone (`search:tune`, G15). The gateway decides — on the
 * first 403 from a write this panel hides the action cells and the form and
 * says who may act, rather than leaving controls that look broken.
 */

type Filter = SearchAliasStatus | 'all';

interface Problem {
  readonly kind: ApiProblemKind;
  readonly detail: string | null;
  readonly correlation_id: string | null;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
}

interface ListState {
  readonly response: SearchAliasListResponse | null;
  readonly busy: boolean;
  readonly problem: Problem | null;
}

type WritePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy'; readonly alias_id: string }
  | { readonly kind: 'problem'; readonly problem: Problem };

type MinePhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'done'; readonly response: SearchAliasMineResponse }
  | { readonly kind: 'problem'; readonly problem: Problem };

const PAGE_LIMIT = 25;

const INITIAL: ListState = { response: null, busy: true, problem: null };

function toProblem(error: unknown): Problem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
    field_errors: fieldErrors,
  };
}

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function statusWord(status: SearchAliasStatus): string {
  return status;
}

/** The verb a row offers, by its derived status. */
function actionFor(status: SearchAliasStatus): { readonly verb: 'Approve' | 'Deactivate' | 'Reactivate' } {
  if (status === 'suggested') return { verb: 'Approve' };
  if (status === 'active') return { verb: 'Deactivate' };
  return { verb: 'Reactivate' };
}

function emptyCopy(filter: Filter) {
  switch (filter) {
    case 'suggested':
      return <p>Nothing to approve. Mined proposals and new manual aliases appear here.</p>;
    case 'active':
      return <p>No active aliases — queries are matched as typed.</p>;
    case 'inactive':
      return <p>No deactivated aliases.</p>;
    default:
      return (
        <p>
          No aliases yet. Add one, or run <em>Mine proposals</em> once searches have been logged.
        </p>
      );
  }
}

/* -------------------------------------------------------------------------- */
/* The panel                                                                   */
/* -------------------------------------------------------------------------- */

export function AliasesPanel() {
  const headingId = useId();
  const [filter, setFilter] = useState<Filter | 'auto'>('auto');
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ListState>(INITIAL);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [write, setWrite] = useState<WritePhase>({ kind: 'idle' });
  const [confirming, setConfirming] = useState<string | null>(null);
  const [writesForbidden, setWritesForbidden] = useState(false);
  const [mine, setMine] = useState<MinePhase>({ kind: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const effectiveFilter: Filter = filter === 'auto' ? 'all' : filter;

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const request: SearchAliasListRequest = {
      ...(effectiveFilter === 'all' ? {} : { status: effectiveFilter }),
      page: cursor === undefined ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor },
    };

    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await listSearchAliases(request, controller.signal);
        if (controller.signal.aborted) return;
        setState({ response, busy: false, problem: null });
        /* The panel opens on the queue when there is one: the first, unfiltered
         * read tells us whether there is, and only then does the filter settle. */
        setFilter((current) => (current === 'auto' ? (response.totals.suggested > 0 ? 'suggested' : 'all') : current));
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ response: null, busy: false, problem: toProblem(error) });
        setFilter((current) => (current === 'auto' ? 'all' : current));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [effectiveFilter, cursor, reloadNonce]);

  const reload = useCallback(() => {
    setReloadNonce((n) => n + 1);
  }, []);

  /** A filter change invalidates every cursor: they are minted per filter set. */
  const chooseFilter = useCallback((next: Filter) => {
    setFilter(next);
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
    setConfirming(null);
  }, []);

  const runWrite = useCallback(
    (alias: SearchAlias) => {
      const { verb } = actionFor(alias.status);
      setConfirming(null);
      setWrite({ kind: 'busy', alias_id: alias.alias_id });
      void (async () => {
        try {
          if (verb === 'Approve') {
            await manageClause({ action: 'approve_search_alias', alias_id: alias.alias_id });
          } else {
            await manageClause({ action: 'set_search_alias_active', alias_id: alias.alias_id, is_active: verb === 'Reactivate' });
          }
          setWrite({ kind: 'idle' });
        } catch (error) {
          const problem = toProblem(error);
          if (problem.kind === 'forbidden') setWritesForbidden(true);
          setWrite({ kind: 'problem', problem });
        } finally {
          /* onSettled: the server's post-image is the only honest row. */
          reload();
        }
      })();
    },
    [reload],
  );

  const runMine = useCallback(() => {
    setMine({ kind: 'busy' });
    void (async () => {
      try {
        const response = await mineSearchAliases();
        setMine({ kind: 'done', response });
      } catch (error) {
        const problem = toProblem(error);
        if (problem.kind === 'forbidden') setWritesForbidden(true);
        setMine({ kind: 'problem', problem });
      } finally {
        reload();
      }
    })();
  }, [reload]);

  const response = state.response;
  const items = response?.items ?? [];
  const totals = response?.totals ?? null;

  const columns: readonly DataTableColumn<SearchAlias>[] = [
    { id: 'alias_term', header: 'Alias term', cell: (row) => <code>{row.alias_term}</code> },
    { id: 'canonical_term', header: 'Canonical term', cell: (row) => row.canonical_term },
    {
      id: 'origin',
      header: 'Origin',
      cell: (row) => <span className={`tag ${row.origin === 'Mined' ? 'ai' : 'out'}`}>{row.origin}</span>,
    },
    { id: 'status', header: 'Status', cell: (row) => <span className="sq-alias-status">{statusWord(row.status)}</span> },
    {
      id: 'proposed',
      header: 'Proposed by / when',
      cell: (row) => (
        <>
          {row.created_by_upn ?? '—'}
          <br />
          <span className="mut">{formatWhen(row.created_utc)}</span>
        </>
      ),
    },
    {
      id: 'approved',
      header: 'Approved by / when',
      cell: (row) => (
        <>
          {row.approved_by_upn ?? '—'}
          <br />
          <span className="mut">{formatWhen(row.approved_utc)}</span>
        </>
      ),
    },
    ...(writesForbidden
      ? []
      : [
          {
            id: 'action',
            header: 'Action',
            cell: (row: SearchAlias) => (
              <RowAction
                alias={row}
                confirming={confirming === row.alias_id}
                busy={write.kind === 'busy'}
                onAsk={() => {
                  setConfirming(row.alias_id);
                }}
                onCancel={() => {
                  setConfirming(null);
                }}
                onConfirm={() => {
                  runWrite(row);
                }}
              />
            ),
          },
        ]),
  ];

  return (
    <section id="aliases" className="sq-aliases card" aria-labelledby={headingId}>
      <div className="sq-aliases-head">
        <h3 id={headingId}>Aliases</h3>
        {writesForbidden ? null : (
          <button type="button" className="btn" disabled={mine.kind === 'busy'} onClick={runMine}>
            Mine proposals
          </button>
        )}
      </div>
      <p className="mut">
        What people type, mapped to what the library calls it. A suggestion is live in search only after a Legal
        reviewer approves it.
      </p>

      {mine.kind === 'done' ? <MineResult response={mine.response} /> : null}
      {mine.kind === 'problem' ? <WriteNotice problem={mine.problem} endpoint="search-aliases-mine" /> : null}

      {writesForbidden ? (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>
              Only Legal reviewers (<code>legal_reviewer</code>, the CEQ-Legal group) can approve or change search
              aliases. You can read them.
            </strong>
          </p>
        </div>
      ) : null}

      {state.busy && response === null && state.problem === null ? (
        <p role="status" aria-busy="true">
          Reading the aliases…
        </p>
      ) : null}

      {state.problem ? (
        <ErrorNotice
          kind={state.problem.kind}
          detail={state.problem.detail}
          correlation_id={state.problem.correlation_id}
          action={
            state.problem.kind === 'not_implemented' ? (
              <p className="mut">
                Waiting on <code>search-aliases</code> (<code>GET /v1/search/aliases</code>).
              </p>
            ) : (
              <button type="button" className="btn" onClick={reload}>
                Retry
              </button>
            )
          }
        />
      ) : null}

      {totals ? (
        <div className="totals sq-alias-tiles" role="group" aria-label="Alias status filter">
          <Tile label="Suggested" value={totals.suggested} tone={totals.suggested > 0 ? 'warn' : 'plain'} pressed={effectiveFilter === 'suggested'} onClick={() => chooseFilter('suggested')} />
          <Tile label="Active" value={totals.active} tone="plain" pressed={effectiveFilter === 'active'} onClick={() => chooseFilter('active')} />
          <Tile label="Inactive" value={totals.inactive} tone="plain" pressed={effectiveFilter === 'inactive'} onClick={() => chooseFilter('inactive')} />
          <button type="button" className="btn sm" aria-pressed={effectiveFilter === 'all'} onClick={() => chooseFilter('all')}>
            All
          </button>
        </div>
      ) : null}

      {write.kind === 'problem' ? <WriteNotice problem={write.problem} endpoint="manage-clause" /> : null}

      {response ? (
        <>
          <DataTable
            caption="Search aliases"
            columns={columns}
            rows={items}
            rowKey={(row) => row.alias_id}
            busy={state.busy}
            empty={emptyCopy(effectiveFilter)}
          />
          {items.length > 0 || stack.cursors.length > 0 ? (
            <CursorPager
              next_cursor={response.page.next_cursor}
              historyDepth={stack.cursors.length}
              busy={state.busy}
              pageNumber={pageNumber(stack)}
              returnedCount={response.page.returned_count}
              totalInScope={null}
              onNext={() => {
                if (response.page.next_cursor === null) return;
                setStack((current) => pushCursor(current, cursor ?? ''));
                setCursor(response.page.next_cursor);
              }}
              onPrevious={() => {
                const popped = popCursor(stack);
                setStack(popped.stack);
                setCursor(popped.cursor === '' ? undefined : popped.cursor);
              }}
            />
          ) : null}
        </>
      ) : null}

      {writesForbidden ? null : <AddAliasForm onCreated={reload} onForbidden={() => setWritesForbidden(true)} />}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                     */
/* -------------------------------------------------------------------------- */

function Tile({
  label,
  value,
  tone,
  pressed,
  onClick,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: 'warn' | 'plain';
  readonly pressed: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button type="button" className="total sq-alias-tile" data-tone={tone} aria-pressed={pressed} onClick={onClick}>
      <span>{label}</span>
      <b>{value}</b>
    </button>
  );
}

/** The row's action cell: one verb, then an inline "Approve? [Yes] [No]" swap — no modal. */
function RowAction({
  alias,
  confirming,
  busy,
  onAsk,
  onCancel,
  onConfirm,
}: {
  readonly alias: SearchAlias;
  readonly confirming: boolean;
  readonly busy: boolean;
  readonly onAsk: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const yesRef = useRef<HTMLButtonElement | null>(null);
  const { verb } = actionFor(alias.status);

  useEffect(() => {
    if (confirming) yesRef.current?.focus();
  }, [confirming]);

  if (confirming) {
    return (
      <span className="sq-alias-confirm">
        {verb}? {' '}
        <button ref={yesRef} type="button" className="btn sm pri" disabled={busy} aria-label={`Yes, ${verb.toLowerCase()} ${alias.alias_term}`} onClick={onConfirm}>
          Yes
        </button>{' '}
        <button type="button" className="btn sm qt" disabled={busy} aria-label={`No, keep ${alias.alias_term} as it is`} onClick={onCancel}>
          No
        </button>
      </span>
    );
  }
  return (
    <button type="button" className="btn sm" disabled={busy} aria-label={`${verb} ${alias.alias_term}`} onClick={onAsk}>
      {verb}
    </button>
  );
}

function MineResult({ response }: { readonly response: SearchAliasMineResponse }) {
  const byReason = (reason: string) => response.skipped.filter((skip) => skip.reason === reason).length;
  if (response.proposed.length === 0) {
    return (
      <p role="status" className="mut sq-alias-mine-result">
        No new proposals — no repeated zero-result query in the last {response.window_days} days is close enough to a
        known term.
      </p>
    );
  }
  return (
    <p role="status" className="mut sq-alias-mine-result">
      Proposed {response.proposed.length}, skipped {response.skipped.length} ({byReason('exists')} exists /{' '}
      {byReason('below_threshold')} below threshold / {byReason('identity')} identity) from the last {response.window_days}{' '}
      days.
    </p>
  );
}

function WriteNotice({ problem, endpoint }: { readonly problem: Problem; readonly endpoint: 'manage-clause' | 'search-aliases-mine' }) {
  if (problem.kind === 'forbidden') {
    /* The panel-level banner says who may act; nothing more to add per write. */
    return null;
  }
  if (problem.kind === 'conflict') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>The alias has moved on</strong>
        </p>
        {problem.field_errors.length === 0 ? <p>{problem.detail}</p> : problem.field_errors.map((entry) => <p key={entry.path}>{entry.message}</p>)}
        <p className="mut">The list has been re-read so the true state shows.</p>
      </div>
    );
  }
  if (problem.kind === 'not_found') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>That alias no longer exists</strong>
        </p>
        <p className="mut">The list has been re-read.</p>
      </div>
    );
  }
  return (
    <ErrorNotice
      kind={problem.kind}
      detail={problem.detail}
      correlation_id={problem.correlation_id}
      action={
        problem.kind === 'not_implemented' ? (
          <p className="mut">
            Waiting on <code>{endpoint}</code>.
          </p>
        ) : undefined
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Add an alias                                                                */
/* -------------------------------------------------------------------------- */

type FormPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'invalid'; readonly message: string }
  | { readonly kind: 'problem'; readonly problem: Problem }
  | { readonly kind: 'created'; readonly alias_term: string; readonly audit_id: string };

function normalise(term: string): string {
  return term.trim().toLowerCase();
}

function AddAliasForm({ onCreated, onForbidden }: { readonly onCreated: () => void; readonly onForbidden: () => void }) {
  const [aliasTerm, setAliasTerm] = useState('');
  const [canonicalTerm, setCanonicalTerm] = useState('');
  const [phase, setPhase] = useState<FormPhase>({ kind: 'idle' });
  const aliasId = useId();
  const canonicalId = useId();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const alias_term = aliasTerm.trim();
    const canonical_term = canonicalTerm.trim();
    if (normalise(alias_term) === normalise(canonical_term)) {
      setPhase({ kind: 'invalid', message: 'The alias and the canonical term are the same.' });
      return;
    }
    const parsed = ManageClauseRequestSchema.safeParse({ action: 'create_search_alias', alias_term, canonical_term });
    if (!parsed.success) {
      setPhase({ kind: 'invalid', message: parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ') });
      return;
    }
    setPhase({ kind: 'busy' });
    void (async () => {
      try {
        const response = await manageClause(parsed.data);
        setPhase({ kind: 'created', alias_term: response.alias?.alias_term ?? alias_term, audit_id: response.audit.audit_id });
        setAliasTerm('');
        setCanonicalTerm('');
        onCreated();
      } catch (error) {
        const problem = toProblem(error);
        if (problem.kind === 'forbidden') onForbidden();
        setPhase({ kind: 'problem', problem });
        if (problem.kind === 'conflict' || problem.kind === 'not_found') onCreated();
      }
    })();
  };

  return (
    <details className="sq-alias-add">
      <summary>Add an alias</summary>
      <form className="cform" onSubmit={submit} aria-label="Add an alias">
        <label htmlFor={aliasId}>
          Alias term
          <input
            id={aliasId}
            type="text"
            value={aliasTerm}
            required
            maxLength={120}
            placeholder="what people type"
            onChange={(e) => setAliasTerm(e.target.value)}
          />
        </label>
        <label htmlFor={canonicalId}>
          Canonical term
          <input
            id={canonicalId}
            type="text"
            value={canonicalTerm}
            required
            maxLength={200}
            placeholder="what the library calls it"
            onChange={(e) => setCanonicalTerm(e.target.value)}
          />
        </label>
        {phase.kind === 'invalid' ? (
          <p className="ferr" role="alert">
            {phase.message}
          </p>
        ) : null}
        {phase.kind === 'problem' ? (
          phase.problem.kind === 'validation' ? (
            <p className="ferr" role="alert">
              {phase.problem.field_errors.length === 0 ? phase.problem.detail : phase.problem.field_errors.map((entry) => entry.message).join('; ')}
            </p>
          ) : (
            <WriteNotice problem={phase.problem} endpoint="manage-clause" />
          )
        ) : null}
        {phase.kind === 'created' ? (
          <p role="status" className="mut">
            Added <code>{phase.alias_term}</code> as a suggestion · audit <code>{phase.audit_id}</code>. Approve it above to
            make it live.
          </p>
        ) : null}
        <div className="cform-acts">
          <button type="submit" className="btn" disabled={phase.kind === 'busy'}>
            Add alias
          </button>
        </div>
      </form>
    </details>
  );
}
